/**
 * Slideshow Countdown Timer
 * Handles countdown timers in slideshow slides
 */

class SlideshowCountdown {
  constructor() {
    this.timers = new Map();
    this.init();
  }

  init() {
    // Initialize all countdown timers on the page
    const countdownElements = document.querySelectorAll('.slideshow-countdown-timer');
    
    countdownElements.forEach(element => {
      this.initCountdown(element);
    });

    // Re-initialize when DOM changes (for dynamic content)
    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const countdowns = node.querySelectorAll ? node.querySelectorAll('.slideshow-countdown-timer') : [];
              countdowns.forEach(el => {
                if (!this.timers.has(el)) {
                  this.initCountdown(el);
                }
              });
            }
          });
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /**
   * Initialize a single countdown timer
   */
  initCountdown(element) {
    const date = element.dataset.countdownDate;
    const time = element.dataset.countdownTime || '23:59';
    const endText = element.dataset.countdownEndText || 'Offer ended!';
    const hideOnEnd = element.dataset.countdownHideOnEnd === 'true';

    if (!date) {
      console.warn('Countdown timer missing end date');
      return;
    }

    // Parse the target date/time
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    
    const targetDate = new Date(year, month - 1, day, hours || 23, minutes || 59, 59);

    // Start the countdown
    const intervalId = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        // Countdown finished
        this.handleCountdownEnd(element, endText, hideOnEnd, intervalId);
        return;
      }

      // Calculate time units
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Update the display
      this.updateDisplay(element, days, hours, minutes, seconds);
    }, 1000);

    // Store the interval ID
    this.timers.set(element, intervalId);

    // Initial update
    const now = new Date().getTime();
    const distance = targetDate.getTime() - now;
    
    if (distance > 0) {
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      this.updateDisplay(element, days, hours, minutes, seconds);
    }
  }

  /**
   * Update countdown display
   */
  updateDisplay(element, days, hours, minutes, seconds) {
    const daysEl = element.querySelector('[data-unit="days"]');
    const hoursEl = element.querySelector('[data-unit="hours"]');
    const minutesEl = element.querySelector('[data-unit="minutes"]');
    const secondsEl = element.querySelector('[data-unit="seconds"]');

    if (daysEl) daysEl.textContent = this.padZero(days);
    if (hoursEl) hoursEl.textContent = this.padZero(hours);
    if (minutesEl) minutesEl.textContent = this.padZero(minutes);
    if (secondsEl) secondsEl.textContent = this.padZero(seconds);
  }

  /**
   * Handle countdown end
   */
  handleCountdownEnd(element, endText, hideOnEnd, intervalId) {
    clearInterval(intervalId);
    this.timers.delete(element);

    if (hideOnEnd) {
      // Hide the entire countdown
      element.style.display = 'none';
    } else {
      // Replace with end text
      element.innerHTML = `<div class="countdown-ended">${endText}</div>`;
    }

    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('countdown-ended', {
      bubbles: true,
      detail: { element, endText }
    }));
  }

  /**
   * Pad numbers with leading zero
   */
  padZero(num) {
    return num.toString().padStart(2, '0');
  }

  /**
   * Stop a specific countdown
   */
  stopCountdown(element) {
    const intervalId = this.timers.get(element);
    if (intervalId) {
      clearInterval(intervalId);
      this.timers.delete(element);
    }
  }

  /**
   * Stop all countdowns
   */
  stopAllCountdowns() {
    this.timers.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.timers.clear();
  }
}

// Initialize countdown timers
document.addEventListener('DOMContentLoaded', () => {
  window.slideshowCountdown = new SlideshowCountdown();
});

// Also initialize immediately if DOMContentLoaded already fired
if (document.readyState === 'interactive' || document.readyState === 'complete') {
  window.slideshowCountdown = new SlideshowCountdown();
}

// Clean up on page unload
window.addEventListener('beforeunload', () => {
  if (window.slideshowCountdown) {
    window.slideshowCountdown.stopAllCountdowns();
  }
});
